/**
 * Mis apuestas — la segunda y última vista de un usuario sin permisos.
 */
import { useMyBets } from '../lib/queries.js';
import { useT } from '../lib/i18n.js';
import { formatMoney, formatOdds, formatDateTime } from '../lib/format.js';
import { DEFAULT_CURRENCY } from '@thepit/contracts';
import { Badge, Card, EmptyState, Skeleton } from '../components/ui.js';

const STATUS_TONE = {
  open: 'neutral',
  won: 'success',
  lost: 'danger',
  refunded: 'warning',
  paid: 'info',
  void: 'neutral',
} as const;

export function MisApuestas(): JSX.Element {
  const t = useT();
  const { data, isLoading } = useMyBets();

  if (isLoading) return <Skeleton height={160} />;

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <Card title={t('bet.mine')}>
        <EmptyState title={t('bet.mineEmpty')} glyph="◈" />
      </Card>
    );
  }

  return (
    <Card title={t('bet.mine')}>
      <ul className="flex flex-col gap-2">
        {items.map((bet) => (
          <li key={bet.id} className="pit-card pit-content-auto flex flex-wrap items-center gap-3 px-3 py-2">
            <span className="pit-mono text-[var(--pit-text-xs)] text-[var(--pit-text-muted)]">{bet.receiptCode}</span>
            <Badge tone={bet.corner === 'red' ? 'danger' : 'info'}>
              {bet.corner === 'red' ? t('fight.red') : t('fight.blue')}
            </Badge>
            <span className="pit-mono text-[var(--pit-text-sm)]">{formatMoney(bet.stakeCents, DEFAULT_CURRENCY)}</span>
            <span className="pit-mono text-[var(--pit-text-xs)] text-[var(--pit-text-muted)]">
              {t('fight.odds')} {formatOdds(bet.oddsBp)}
            </span>
            <span className="pit-mono text-[var(--pit-text-sm)] text-[var(--pit-accent-amber)]">
              {formatMoney(bet.payoutCents ?? bet.estPayoutCents, DEFAULT_CURRENCY)}
            </span>
            <Badge tone={STATUS_TONE[bet.status]}>{t(`bet.status.${bet.status}`)}</Badge>
            <span className="pit-mono ml-auto text-[var(--pit-text-2xs)] text-[var(--pit-text-muted)]">
              {formatDateTime(bet.createdAt)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
